/**
 * overview-tab.tsx — Tab "Tổng quan": thông tin tổ chức, gói + trạng thái cấp phép,
 * hạn mức vs mức dùng (cùng công thức app áp — billing-service getBillingSummary), số liệu nhanh.
 */
import { Contact, MessageSquare, MessagesSquare, Users } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { StatCard } from '@/components/shared/stat-card'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { cn, formatNumber } from '@/lib/utils'
import { useOrgOverview, type BillingSummary } from '@/hooks/use-platform-org-manage'
import { OrgStatusBadge } from '../org-status'
import { fmtDateTime, fmtDay, limitText } from './labels'
import { displayVnPhone } from '@/pages/team/add-member-helpers'
import { COMPANY_FIELDS } from '@/pages/settings/settings-utils'

export function OverviewTab({ orgId }: { orgId: string }) {
  const { data, isLoading, isError } = useOrgOverview(orgId)
  if (isLoading) return <Loading label="Đang tải..." />
  if (isError || !data) return <ErrorState message="Không tải được tổng quan tổ chức." />
  const { org, owners, billing, stats, company } = data
  const companyRows = COMPANY_FIELDS.filter((f) => company?.[f.key as keyof typeof company])

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Nhân viên" value={stats.members} icon={Users} />
        <StatCard label="Khách hàng" value={stats.contacts} icon={Contact} />
        <StatCard label="Hội thoại" value={stats.conversations} icon={MessagesSquare} />
        <StatCard label="Tin nhắn 30 ngày" value={stats.messages30d} icon={MessageSquare} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Thông tin tổ chức</CardTitle></CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Tên</dt><dd className="font-medium">{org.name}</dd>
              <dt className="text-muted-foreground">Mã tổ chức</dt><dd className="font-mono text-xs break-all">{org.id}</dd>
              <dt className="text-muted-foreground">Ngày tạo</dt><dd>{fmtDateTime(org.createdAt)}</dd>
              <dt className="text-muted-foreground">Chủ sở hữu</dt>
              <dd className="space-y-1">
                {owners.length === 0 && <span className="text-destructive">Chưa có chủ sở hữu</span>}
                {owners.map((o) => (
                  <div key={o.id} className="flex flex-wrap items-center gap-1.5">
                    <span>{o.fullName}</span>
                    <span className="text-xs text-muted-foreground">{o.email}</span>
                    {o.phone && <span className="text-xs text-muted-foreground">· {displayVnPhone(o.phone)}</span>}
                    {!o.isActive && <Badge variant="destructive">Đã khoá</Badge>}
                  </div>
                ))}
              </dd>
              {companyRows.map((f) => (
                <div key={f.key} className="contents">
                  <dt className="text-muted-foreground">{f.label}</dt>
                  <dd className="break-words">{company?.[f.key as keyof typeof company]}</dd>
                </div>
              ))}
              <dt className="text-muted-foreground">Vai trò hệ thống</dt>
              <dd>
                {stats.systemRoles >= 4
                  ? `${stats.systemRoles} vai trò gốc`
                  : <span className="text-warning">Thiếu vai trò gốc ({stats.systemRoles}/4) — khởi tạo lại ở tab Cài đặt</span>}
              </dd>
              <dt className="text-muted-foreground">Ghi chú nội bộ</dt>
              <dd className="whitespace-pre-wrap">{org.adminNotes || <span className="text-muted-foreground">—</span>}</dd>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Gói & cấp phép</CardTitle></CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Trạng thái</dt>
              <dd className="flex flex-wrap items-center gap-1.5">
                <OrgStatusBadge status={org.displayStatus} />
                {org.expiringSoon && <Badge variant="warning">Sắp hết hạn</Badge>}
              </dd>
              <dt className="text-muted-foreground">Nhãn gói</dt><dd>{org.plan || <span className="text-muted-foreground">(trống → Miễn phí)</span>}</dd>
              <dt className="text-muted-foreground">Gói hiệu lực</dt>
              <dd className="flex flex-wrap items-center gap-1.5">
                {billing?.planName ?? (billing?.effectivePlan ? billing.effectivePlan : 'Thoả thuận riêng (không giới hạn)')}
                {billing?.wasDowngraded && <Badge variant="warning">Gói trả phí đã hết hạn → Miễn phí</Badge>}
              </dd>
              <dt className="text-muted-foreground">Hết hạn</dt><dd>{org.expiresAt ? fmtDay(org.expiresAt) : 'Không giới hạn'}</dd>
            </dl>
          </CardContent>
        </Card>
      </div>

      {billing && <QuotaCard billing={billing} />}
    </div>
  )
}

function QuotaCard({ billing }: { billing: BillingSummary }) {
  const { usage, limits } = billing
  const rows: Array<{ label: string; used: number; limit: number | null; hint?: string }> = [
    { label: 'Nhân viên (đang hoạt động)', used: usage.members, limit: limits.members },
    { label: 'Kênh chính thức', used: usage.channelsOfficial, limit: limits.officialChannels },
    { label: 'Tài khoản cá nhân', used: usage.channelsPersonal, limit: limits.personalChannels },
    { label: 'Khách hoạt động (30 ngày)', used: usage.activeContacts30d, limit: limits.activeContactsPerMonth },
    {
      label: 'AI trả lời hôm nay',
      used: usage.aiToday,
      limit: usage.aiDailyLimit,
      hint: `Trần AI của tổ chức (Cài đặt → Hạn mức AI)${limits.aiRepliesPerDay != null ? ` · gói: ${formatNumber(limits.aiRepliesPerDay)}/ngày` : ''}`,
    },
  ]
  return (
    <Card>
      <CardHeader>
        <CardTitle>Hạn mức & mức sử dụng</CardTitle>
        <p className="text-xs text-muted-foreground">
          Cùng công thức trang Gói dịch vụ của khách: số đã mua trong đơn còn hiệu lực, không có thì số gồm sẵn của gói. ∞ = không giới hạn.
          {billing.purchased && ' Có số lượng mua thêm.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {rows.map((r) => {
          const pct = r.limit ? Math.min(100, Math.round((r.used / r.limit) * 100)) : 0
          const over = r.limit != null && r.used > r.limit
          return (
            <div key={r.label} className="space-y-1">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span>{r.label}</span>
                <span className={cn('tabular-nums', over && 'font-medium text-destructive')}>
                  {formatNumber(r.used)} / {limitText(r.limit)}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className={cn('h-full rounded-full', over ? 'bg-destructive' : pct >= 80 ? 'bg-warning' : 'bg-primary')}
                  style={{ width: r.limit == null ? '0%' : `${Math.max(pct, r.used > 0 ? 2 : 0)}%` }}
                />
              </div>
              {r.hint && <p className="text-xs text-muted-foreground">{r.hint}</p>}
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}
